/**
 * In-place custom element class patching.
 *
 * Vite HMR re-executes an edited component module, which calls
 * `customElements.define()` again with a freshly evaluated class. The
 * platform registry can't re-define a tag, and live instances keep their
 * original prototype chain, so instead of swapping classes we intercept the
 * duplicate define and patch the originally-registered class in place:
 * copy prototype + static descriptors from the new class onto the old one
 * and nudge live instances to re-render.
 *
 * This module is dependency-free and duck-typed against ReactiveElement so
 * it never imports lit (keeps it version-agnostic and safe for non-Lit
 * custom elements, which get prototype/static patching only).
 */

import {
  createChildState,
  type ChildState,
  type ChildStateMode,
} from './child-state.js';
import {subscribeOverrideKeys} from './overrides.js';
import {hasServerRender} from './server-render.js';
import {pageChannel} from './page-channel.js';
import {PAGE_ID} from './page-id.js';
import type {PageTransport, ViteHotLike} from './page-channel.js';
import {
  HMR_INCOMPATIBLE_CHANNEL,
  describeHmrReason,
  type HmrIncompatibilityEvent,
  type HmrIncompatibilityReason,
} from '../../types/hmr-incompatibility.js';
import {HMR_PATCH_CHANNEL} from '../../types/hmr-patch.js';
import type {HmrPatchEvent} from '../../types/hmr-patch.js';

/** What the patcher needs of the page channel: reporting incompatibilities. */
type HotChannel = Pick<PageTransport, 'send'>;

export interface PatchOptions {
  /**
   * Cycle `disconnectedCallback()`/`connectedCallback()` on live instances
   * after a hot patch. Defaults to `false`.
   */
  reconnect?: boolean;

  /**
   * What to do when a component can't be hot-patched in place (e.g.
   * standard `accessor` decorators). Defaults to `'reload'`.
   */
  onIncompatible?: 'reload' | 'warn';

  /**
   * What happens to custom elements a hot patch re-creates (an edited
   * template clones fresh DOM): `'reset'` leaves them at their defaults,
   * `'transfer'` copies the old element's state onto the new one, `'reuse'`
   * puts the old element back where the new one carries no bindings (and
   * transfers otherwise). Defaults to `'transfer'`.
   */
  childState?: ChildStateMode;
}

interface ReactiveElementLike extends HTMLElement {
  requestUpdate?: () => void;
  renderRoot?: unknown;
  hasUpdated?: boolean;
  connectedCallback?: () => void;
  disconnectedCallback?: () => void;
}

/** Duck-typed view of ReactiveElement constructor statics. */
interface ReactiveCtorLike extends CustomElementConstructor {
  finalize?: () => void;
  elementProperties?: Map<PropertyKey, unknown>;
  elementStyles?: ReadonlyArray<unknown>;
  observedAttributes?: readonly string[];
  _initializers?: Array<(element: unknown) => void>;
}

interface TagRecord {
  tagName: string;
  canonical: CustomElementConstructor;
  instances: Set<ReactiveElementLike>;
  generation: number;
}

interface PatchState {
  records: Map<string, TagRecord>;
  /** Generation a (possibly detached) instance last caught up to. */
  generationOf: WeakMap<ReactiveElementLike, number>;
  options: Required<PatchOptions>;
  /** Set in `install()`; reports HMR-incompatibility events when present. */
  hot?: HotChannel;
  /** Pairs re-created child elements with the ones they replace. */
  childState?: ChildState;
  /**
   * The tag of each class defined since install, every later version
   * included, which the registry never sees: the canonical class stands in
   * for it once patched. Read by `customElements.getName()`.
   */
  tagOfVersion: WeakMap<CustomElementConstructor, string>;
}

type LifecycleName = 'connectedCallback' | 'disconnectedCallback';

interface BrandedWrapper {
  (this: ReactiveElementLike): void;
  [RECORD_BRAND]?: TagRecord;
}

// Exactly one patch state per page, even if this module loads twice.
const STATE_KEY = Symbol.for('@oddsquad/vite-plugin-lit#patch');
// Brands our lifecycle wrappers so re-instrumentation is idempotent.
const RECORD_BRAND = Symbol.for('@oddsquad/vite-plugin-lit#wrapperRecord');

const PROTO_SKIP: ReadonlyArray<PropertyKey> = ['constructor'];
const STATIC_SKIP: ReadonlyArray<PropertyKey> = ['prototype', 'name', 'length'];

const ownKeys = (o: object): PropertyKey[] => [
  ...Object.getOwnPropertyNames(o),
  ...Object.getOwnPropertySymbols(o),
];

/**
 * Syncs own members `source` → `target`: deletes own keys of `target` that
 * vanished from `source`, then copies every own descriptor of `source`.
 * Individual copies are best-effort (non-configurable slots are skipped).
 */
// Exported for unit testing; otherwise module-internal.
export const syncOwnMembers = (
  target: object,
  source: object,
  skip: ReadonlyArray<PropertyKey>
) => {
  for (const key of ownKeys(target)) {
    if (skip.includes(key)) {
      continue;
    }
    if (Object.getOwnPropertyDescriptor(source, key) === undefined) {
      try {
        delete (target as Record<PropertyKey, unknown>)[key];
      } catch {
        // Non-configurable; leave it.
      }
    }
  }
  for (const key of ownKeys(source)) {
    if (skip.includes(key)) {
      continue;
    }
    try {
      Object.defineProperty(
        target,
        key,
        Object.getOwnPropertyDescriptor(source, key)!
      );
    } catch {
      // Non-configurable; leave it.
    }
  }
};

/**
 * Defines branded `connectedCallback`/`disconnectedCallback` wrappers on the
 * prototype to maintain the live-instance set and generation stamps. Called
 * at first define and again after every patch (descriptor sync replaces or
 * deletes the wrappers).
 */
const instrument = (
  state: PatchState,
  proto: object,
  record: TagRecord
): void => {
  for (const name of [
    'connectedCallback',
    'disconnectedCallback',
  ] as const satisfies readonly LifecycleName[]) {
    const own = Object.getOwnPropertyDescriptor(proto, name);
    if ((own?.value as BrandedWrapper | undefined)?.[RECORD_BRAND] === record) {
      continue;
    }
    // May be an own raw function (just copied from the new class), an
    // inherited base implementation, or absent entirely.
    const original = (proto as Record<LifecycleName, (() => void) | undefined>)[
      name
    ];
    const wrapper: BrandedWrapper =
      name === 'connectedCallback'
        ? function (this: ReactiveElementLike) {
            // Before the original runs, so a transfer lands ahead of the
            // element's first update.
            state.childState?.connected(this, !state.generationOf.has(this));
            record.instances.add(this);
            const stamp = state.generationOf.get(this);
            if (stamp !== undefined && stamp !== record.generation) {
              // Was detached during one or more patches; catch up.
              this.requestUpdate?.();
            }
            state.generationOf.set(this, record.generation);
            adoptAfterHydration(this);
            original?.call(this);
          }
        : function (this: ReactiveElementLike) {
            record.instances.delete(this);
            state.childState?.disconnected(this);
            original?.call(this);
          };
    wrapper[RECORD_BRAND] = record;
    try {
      Object.defineProperty(proto, name, {
        value: wrapper,
        writable: true,
        configurable: true,
      });
    } catch {
      // Non-configurable lifecycle slot; tracking degrades gracefully.
    }
  }
};

/**
 * Runs a class's decorator initializers on a live instance and returns the
 * contexts that new `@lit/context` providers announced while they ran: a
 * `ContextProvider` dispatches a `context-provider` event from its host when
 * it connects.
 */
const runInitializers = (
  el: ReactiveElementLike,
  initializers: ReadonlyArray<(element: unknown) => void>
): unknown[] => {
  const provided: unknown[] = [];
  const announced = (ev: Event) => {
    if (ev.target === el) provided.push((ev as {context?: unknown}).context);
  };
  el.addEventListener('context-provider', announced);
  try {
    for (const initialize of initializers) initialize(el);
  } finally {
    el.removeEventListener('context-provider', announced);
  }
  return provided;
};

/**
 * Moves an instance's context consumers onto the providers its patch just
 * created. Consumers stay subscribed to the provider of the previous
 * evaluation, which the new class's accessor no longer updates, so they would
 * keep the value they had at the patch.
 *
 * `@lit/context` already re-parents consumers when another provider of the
 * same context appears: each provider answers a `context-provider` event
 * whose source isn't its own host by asking its subscribers to request the
 * context again. The request reaches every provider on the host, oldest
 * first, and a consumer keeps the last one that answers: the newest. So one
 * such event per context, on this host only and from a source that is not
 * the host, does it, without importing the library.
 */
const reparentConsumers = (
  el: ReactiveElementLike,
  contexts: readonly unknown[]
): void => {
  for (const context of new Set(contexts)) {
    const ev = Object.assign(new Event('context-provider'), {
      context,
      // Any source but the host; providers ignore their own host's events.
      contextTarget: REPARENT_SOURCE,
    });
    el.dispatchEvent(ev);
  }
};

/** Stands in for a child provider in {@link reparentConsumers}. */
const REPARENT_SOURCE = {};

/**
 * Detects standard-decorator (`accessor`) reactive properties on the new
 * class. Those close over per-class-evaluation private slots, so copied
 * accessors would brand-check-throw — in-place patching is impossible.
 */
const usesStandardDecorators = (ctor: ReactiveCtorLike): boolean => {
  const metadataSymbol = (Symbol as {metadata?: symbol}).metadata;
  if (metadataSymbol === undefined) {
    return false;
  }
  const metadata = Object.getOwnPropertyDescriptor(ctor, metadataSymbol)
    ?.value as object | undefined;
  if (metadata === undefined || metadata === null) {
    return false;
  }
  // litPropertyMetadata is a Lit internal (not public API). Guard against
  // rename, removal, or shape changes — a false negative here means we try
  // to hot-patch and crash, which is handled by the outer catch in hotPatch.
  try {
    const litPropertyMetadata = (
      globalThis as {
        litPropertyMetadata?: WeakMap<object, Map<unknown, unknown>>;
      }
    ).litPropertyMetadata;
    const properties = litPropertyMetadata?.get(metadata);
    return properties !== undefined && properties.size > 0;
  } catch {
    return false;
  }
};

/**
 * Resolves a static `elementStyles` array to its constructed `CSSStyleSheet`s.
 * Returns `null` for a non-array or any shimmed sheet (no constructible
 * stylesheet support), so callers can bail rather than adopt a partial list.
 */
const stylesToSheets = (styles: unknown): CSSStyleSheet[] | null => {
  if (!Array.isArray(styles)) return null;
  const sheets: CSSStyleSheet[] = [];
  for (const style of styles) {
    const sheet =
      style instanceof CSSStyleSheet
        ? style
        : ((style as {styleSheet?: CSSStyleSheet} | null)?.styleSheet ??
          undefined);
    if (!(sheet instanceof CSSStyleSheet)) return null;
    sheets.push(sheet);
  }
  return sheets;
};

/**
 * Re-adopts constructed stylesheets on an instance's shadow root from the
 * (just-patched) static `elementStyles`. Local reimplementation — no lit
 * import. Skips cleanly for light-DOM components and shimmed sheets.
 *
 * `oldSheets` are the sheets the pre-patch class contributed (captured before
 * the static sync). We drop exactly those and append the new set, leaving any
 * sheet adopted imperatively by other code in place rather than clobbering the
 * whole list.
 */
const readoptStyles = (
  el: ReactiveElementLike,
  oldSheets: CSSStyleSheet[]
): void => {
  const root = el.renderRoot;
  if (typeof ShadowRoot === 'undefined' || !(root instanceof ShadowRoot)) {
    return;
  }
  const sheets = stylesToSheets(
    (el.constructor as ReactiveCtorLike).elementStyles
  );
  if (sheets === null) {
    return;
  }
  const drop = new Set(oldSheets);
  const ours = new Set(sheets);
  // Our styles go first, matching Lit's own order (extra sheets appended).
  const preserved = [...root.adoptedStyleSheets].filter(
    (s) => !drop.has(s) && !ours.has(s)
  );
  root.adoptedStyleSheets = [...sheets, ...preserved];
};

/** What `@lit-labs/ssr-client` throws when the template and the HTML differ. */
const isHydrationMismatch = (error: unknown): boolean =>
  error instanceof Error &&
  error.message.startsWith('Hydration value mismatch');

/**
 * A page loaded between an edit reaching the server and reaching the
 * browser carries HTML from the old template, which the new one cannot
 * hydrate. Lit throws and leaves the server's DOM behind, and the next
 * render appends a second copy beside it. A one-shot own `update` catches
 * that first mismatch, clears the stale DOM and runs the update again,
 * which now renders the template from scratch, as a browser-only page
 * would. There is no client state to lose before the first update.
 */
const recoverFromMismatch = (el: ReactiveElementLike): void => {
  if (Object.hasOwn(el, 'update')) return;
  Object.defineProperty(el, 'update', {
    configurable: true,
    writable: true,
    value(this: ReactiveElementLike, changed: unknown) {
      delete (this as {update?: unknown}).update;
      const update = (
        Object.getPrototypeOf(this) as {update?: (c: unknown) => unknown}
      ).update;
      try {
        return update?.call(this, changed);
      } catch (error) {
        const root = this.shadowRoot;
        if (!isHydrationMismatch(error) || root == null) throw error;
        console.warn(
          `[lit-plugin] <${this.localName}>: the page's server-rendered HTML ` +
            `doesn't match the component's template (most often an edit ` +
            `that landed while the page loaded), so it renders in the ` +
            `browser instead of hydrating.`
        );
        root.replaceChildren();
        // Hydration may have bound its root part before it threw.
        delete (root as {_$litPart$?: unknown})._$litPart$;
        return update?.call(this, changed);
      }
    },
  });
};

/** Server-rendered elements already waiting for {@link adoptAfterHydration}. */
const hydrating = new WeakSet<ReactiveElementLike>();

/**
 * Hands a hydrated element's styles to its shadow root the way a client
 * render would, so hot swaps reach it. Hydration keeps the `<style>` the
 * server wrote into the declarative shadow root and adopts nothing, so a
 * `?css-sheet` edit, which `replaceSync()`s the shared sheet in place, and
 * a patch's style re-adoption never touched a server-rendered element.
 * Once its first update (the hydration) has run, its `elementStyles` are
 * adopted and the server's leading `<style>` goes; Lit's parts all sit
 * after it.
 */
const adoptAfterHydration = (el: ReactiveElementLike): void => {
  if (el.hasUpdated === true || hydrating.has(el)) return;
  const root = el.shadowRoot;
  if (root == null || !hasServerRender(root)) return;
  const done = (el as {updateComplete?: Promise<unknown>}).updateComplete;
  if (done === undefined) return;
  hydrating.add(el);
  recoverFromMismatch(el);
  void Promise.resolve(done).then(() => {
    hydrating.delete(el);
    const sheets = stylesToSheets(
      (el.constructor as ReactiveCtorLike).elementStyles
    );
    if (sheets === null || el.renderRoot !== root) return;
    const ours = new Set(sheets);
    root.adoptedStyleSheets = [
      ...sheets,
      ...[...root.adoptedStyleSheets].filter((s) => !ours.has(s)),
    ];
    // A copy: removing from the live list while walking it skips nodes.
    for (const node of Array.from(root.childNodes)) {
      if (node.nodeType === 8) break;
      if ((node as Element).localName === 'style') node.remove();
    }
  });
};

/**
 * Builds the structured event reported over {@link HMR_INCOMPATIBLE_CHANNEL}.
 * Exported for unit testing; otherwise module-internal. Whether the send
 * survives the `location.reload()` that may follow it can only be checked by
 * hand against a real dev server.
 */
const buildHmrIncompatibleEvent = (
  tagName: string,
  reason: HmrIncompatibilityReason,
  action: HmrIncompatibilityEvent['action']
): HmrIncompatibilityEvent => ({tagName, time: Date.now(), reason, action});

const incompatible = (
  state: PatchState,
  tagName: string,
  reason: HmrIncompatibilityReason
): void => {
  const message = describeHmrReason(reason);
  const action = state.options.onIncompatible === 'warn' ? 'warn' : 'reload';
  state.hot?.send(HMR_INCOMPATIBLE_CHANNEL, {
    ...buildHmrIncompatibleEvent(tagName, reason, action),
    pageId: PAGE_ID,
  });
  if (state.options.onIncompatible === 'warn') {
    console.warn(
      `[lit-plugin] <${tagName}> can't be hot-patched (${message}). ` +
        `Reload the page to pick up the change.`
    );
  } else {
    console.info(
      `[lit-plugin] <${tagName}>: ${message} — performing full reload.`
    );
    location.reload();
  }
};

/**
 * Snapshots reactive property values per live instance through the OLD
 * accessors. Vite serves prod lit in dev (no `development` export
 * condition), where accessor storage keys are unique Symbol()s per class
 * evaluation — without this, state resets.
 */
const snapshotInstanceValues = (
  record: TagRecord,
  OldClass: ReactiveCtorLike,
  NewClass: ReactiveCtorLike
): Map<ReactiveElementLike, Map<PropertyKey, unknown>> => {
  const propertyKeys = new Set<PropertyKey>([
    ...(OldClass.elementProperties?.keys() ?? []),
    ...(NewClass.elementProperties?.keys() ?? []),
  ]);
  const snapshots = new Map<ReactiveElementLike, Map<PropertyKey, unknown>>();
  for (const el of record.instances) {
    const values = new Map<PropertyKey, unknown>();
    for (const key of propertyKeys) {
      values.set(key, (el as unknown as Record<PropertyKey, unknown>)[key]);
    }
    snapshots.set(el, values);
  }
  return snapshots;
};

/**
 * Re-parents the prototype and static chains of `OldClass` onto those of
 * `NewClass`. Mixins regenerate intermediate classes on every module
 * evaluation, so the parents usually differ.
 */
const reparentClassChains = (
  OldClass: ReactiveCtorLike,
  NewClass: ReactiveCtorLike
): void => {
  const newProtoParent = Object.getPrototypeOf(NewClass.prototype) as object;
  if (Object.getPrototypeOf(OldClass.prototype) !== newProtoParent) {
    Object.setPrototypeOf(OldClass.prototype, newProtoParent);
  }
  const newStaticParent = Object.getPrototypeOf(NewClass) as object;
  if (Object.getPrototypeOf(OldClass) !== newStaticParent) {
    Object.setPrototypeOf(OldClass, newStaticParent);
  }
};

/**
 * Module-local `instanceof NewClass` checks keep working against instances
 * of the canonical class.
 */
const aliasInstanceOf = (
  OldClass: ReactiveCtorLike,
  NewClass: ReactiveCtorLike
): void => {
  Object.defineProperty(NewClass, Symbol.hasInstance, {
    value: (instance: unknown) =>
      typeof instance === 'object' &&
      instance !== null &&
      OldClass.prototype.isPrototypeOf(instance),
    configurable: true,
  });
};

/**
 * The platform snapshots observedAttributes at define time; new attribute
 * observations can't take effect without a reload, so a change is only
 * reported (the patch itself proceeds).
 */
const reportObservedAttributesChange = (
  state: PatchState,
  tagName: string,
  oldObserved: string[],
  newObserved: string[]
): void => {
  if (
    oldObserved.length === newObserved.length &&
    oldObserved.every((attr, i) => attr === newObserved[i])
  ) {
    return;
  }
  console.info(
    `[lit-plugin] <${tagName}> changed observedAttributes; the ` +
      `platform registry can't pick this up — reload recommended.`
  );
  state.hot?.send(
    HMR_INCOMPATIBLE_CHANNEL,
    buildHmrIncompatibleEvent(
      tagName,
      {code: 'observed-attributes-changed'},
      'none'
    )
  );
};

/**
 * Restores snapshotted values through the new accessors — only keys the new
 * class still declares.
 */
const restoreValues = (
  el: ReactiveElementLike,
  values: Map<PropertyKey, unknown>,
  newPropertyKeys: Iterable<PropertyKey>
): void => {
  for (const key of newPropertyKeys) {
    if (values.has(key)) {
      (el as unknown as Record<PropertyKey, unknown>)[key] = values.get(key);
    }
  }
};

/**
 * The base `createRenderRoot`: the top-most class in `el`'s chain that has
 * one, which is ReactiveElement's. It returns the existing shadow root with
 * the element's styles adopted, and, unlike the override
 * `@lit-labs/ssr-client`'s hydrate support puts on LitElement, never marks
 * the element for hydration.
 */
const baseCreateRenderRoot = (
  el: ReactiveElementLike
): ((this: ReactiveElementLike) => unknown) | undefined => {
  let found: unknown;
  for (
    let proto = Object.getPrototypeOf(el) as object | null;
    proto !== null;
    proto = Object.getPrototypeOf(proto) as object | null
  ) {
    const own = Object.getOwnPropertyDescriptor(proto, 'createRenderRoot');
    if (typeof own?.value === 'function') found = own.value;
  }
  return found as ((this: ReactiveElementLike) => unknown) | undefined;
};

/**
 * A server-rendered element the patch reaches before it hydrated: held back
 * by `defer-hydration`, so it has no render root and has never updated. Its
 * shadow root holds the old code's HTML, which the hydration on its first
 * update would try to adopt for the new template, and fail. It has no client
 * state yet, so when it wakes it renders itself instead: an own, one-shot
 * `createRenderRoot` clears the stale HTML and hands over the shadow root
 * without marking it for hydration. Until then the server's HTML stays up.
 */
const renderFreshOnWake = (el: ReactiveElementLike): void => {
  if (el.hasUpdated === true || el.renderRoot !== undefined) return;
  if (Object.hasOwn(el, 'createRenderRoot')) return;
  const root = el.shadowRoot;
  if (root == null || !hasServerRender(root)) return;
  const create = baseCreateRenderRoot(el);
  if (create === undefined) return;
  Object.defineProperty(el, 'createRenderRoot', {
    configurable: true,
    writable: true,
    value(this: ReactiveElementLike) {
      delete (this as {createRenderRoot?: unknown}).createRenderRoot;
      this.shadowRoot?.replaceChildren();
      return create.call(this);
    },
  });
};

/**
 * Updates live instances. Re-renders may re-create child elements (edited
 * templates clone fresh DOM); the child-state window pairs them up.
 */
const updateInstances = (
  state: PatchState,
  record: TagRecord,
  OldClass: ReactiveCtorLike,
  NewClass: ReactiveCtorLike,
  snapshots: Map<ReactiveElementLike, Map<PropertyKey, unknown>>,
  oldSheets: CSSStyleSheet[]
): void => {
  state.childState?.open();
  const newPropertyKeys = NewClass.elementProperties?.keys() ?? [];
  // After the static sync this is the NEW class's initializer list.
  const initializers = OldClass._initializers;
  // Snapshot: the reconnect below mutates record.instances mid-iteration.
  // oxlint-disable-next-line unicorn/no-useless-spread
  for (const el of [...record.instances]) {
    if (state.options.reconnect) {
      el.disconnectedCallback?.();
      el.connectedCallback?.();
    }
    // Decorator-created reactive controllers (e.g. @lit/context's
    // @provide/@consume) live in per-class-evaluation closures keyed by
    // instance via addInitializer. Re-running the initializers enrolls
    // live instances in the new closures — without this, the first
    // assignment through a copied accessor throws and we'd full-reload.
    // Controllers from previous evaluations stay attached but inert
    // (everything flows through the newest closures); that's bounded by
    // edit count and dev-only. Runs before the value restore so e.g. a
    // re-created ContextProvider exists when the restore pushes into it.
    const provided = Array.isArray(initializers)
      ? runInitializers(el, initializers)
      : [];
    const values = snapshots.get(el);
    if (values !== undefined) {
      restoreValues(el, values, newPropertyKeys);
    }
    // After the restore, so the consumers it moves read the restored
    // value from the new provider rather than its empty initial one.
    reparentConsumers(el, provided);
    readoptStyles(el, oldSheets);
    renderFreshOnWake(el);
    state.generationOf.set(el, record.generation);
    el.requestUpdate?.();
  }
  // Read after requestUpdate() so each promise covers the new update.
  state.childState?.watch(record.instances);
};

/**
 * The one success report: everything before it has landed. A failure to
 * report must not turn a landed patch into a "patch-failed" reload, so it is
 * swallowed here rather than reaching the caller's catch.
 */
const reportPatched = (
  state: PatchState,
  record: TagRecord,
  startedAt: number
): void => {
  try {
    const patched: HmrPatchEvent = {
      tagName: record.tagName,
      instances: record.instances.size,
      generation: record.generation,
      durationMs: Math.round((performance.now() - startedAt) * 100) / 100,
      childState: state.options.childState,
      at: Date.now(),
    };
    // Stamped with the document: every open tab applies the same update.
    state.hot?.send(HMR_PATCH_CHANNEL, {...patched, pageId: PAGE_ID});
  } catch {
    // Reporting is best-effort; the patch already succeeded.
  }
};

/**
 * Patches the originally-registered class for `record.tagName` in place
 * from the freshly evaluated `NewClass`, then updates live instances.
 */
const hotPatch = (
  state: PatchState,
  record: TagRecord,
  NewClass: ReactiveCtorLike
): void => {
  const OldClass = record.canonical as ReactiveCtorLike;
  const startedAt = performance.now();
  try {
    // 1. Materialize finalized/elementProperties/elementStyles/
    //    __attributeToPropertyMap on the new class before we copy statics.
    if (typeof NewClass.finalize === 'function') {
      NewClass.finalize();
    }

    // 2. Standard-decorator `accessor` properties can't be patched in place.
    if (usesStandardDecorators(NewClass)) {
      incompatible(state, record.tagName, {code: 'accessor-decorators'});
      return;
    }

    const oldObserved = [...(OldClass.observedAttributes ?? [])];
    const newObserved = [...(NewClass.observedAttributes ?? [])];

    // 3. Snapshot reactive property values per live instance.
    const snapshots = snapshotInstanceValues(record, OldClass, NewClass);

    // 4. Re-parent prototype chains.
    reparentClassChains(OldClass, NewClass);

    // Capture the sheets the OLD class contributed before the static sync
    // overwrites `elementStyles`, so readoptStyles can drop exactly those and
    // leave imperatively-adopted sheets in place.
    const oldSheets = stylesToSheets(OldClass.elementStyles) ?? [];

    // 5. Sync own members new → old on the prototype and on statics.
    syncOwnMembers(OldClass.prototype, NewClass.prototype, PROTO_SKIP);
    syncOwnMembers(OldClass, NewClass, STATIC_SKIP);

    // 6. The sync replaced/removed our lifecycle wrappers; re-instrument.
    instrument(state, OldClass.prototype, record);
    record.generation++;
    aliasInstanceOf(OldClass, NewClass);
    reportObservedAttributesChange(
      state,
      record.tagName,
      oldObserved,
      newObserved
    );

    // 7. Update live instances.
    updateInstances(state, record, OldClass, NewClass, snapshots, oldSheets);

    reportPatched(state, record, startedAt);
  } catch (e) {
    incompatible(state, record.tagName, {
      code: 'patch-failed',
      detail: e instanceof Error ? e.message : String(e),
    });
  }
};

/**
 * Installs the `customElements.define` interceptor. Idempotent (per-page
 * global guard); no-ops in environments without `customElements` (SSR).
 */
export const install = (options: PatchOptions = {}): void => {
  if (typeof customElements === 'undefined') {
    return;
  }
  const g = globalThis as unknown as Record<symbol, PatchState | undefined>;
  const existing = g[STATE_KEY];
  if (existing !== undefined) {
    // Already installed; refresh explicitly-provided options only.
    if (options.reconnect !== undefined) {
      existing.options.reconnect = options.reconnect;
    }
    if (options.onIncompatible !== undefined) {
      existing.options.onIncompatible = options.onIncompatible;
    }
    if (options.childState !== undefined) {
      existing.options.childState = options.childState;
    }
    return;
  }
  const hot = (import.meta as {hot?: ViteHotLike}).hot;
  const state: PatchState = {
    records: new Map(),
    generationOf: new WeakMap(),
    options: {
      reconnect: options.reconnect ?? false,
      onIncompatible: options.onIncompatible ?? 'reload',
      childState: options.childState ?? 'transfer',
    },
    hot: hot === undefined ? undefined : pageChannel,
    tagOfVersion: new WeakMap(),
  };
  state.childState = createChildState({
    mode: () => state.options.childState,
  });
  g[STATE_KEY] = state;

  // Let the DevTools panel override these behaviours live (and persist across
  // reloads) on top of the config-time defaults above.
  subscribeOverrideKeys(hot, {
    hmrReconnect: (v) => (state.options.reconnect = v),
    hmrOnIncompatible: (v) => (state.options.onIncompatible = v),
    // Read on every patch (see createChildState's `mode`), so it applies from
    // the next edit on.
    hmrChildState: (v) => (state.options.childState = v),
  });

  // Captured to re-invoke below as nativeDefine.call(this, ...).
  // oxlint-disable-next-line typescript/unbound-method
  const nativeDefine = customElements.define;
  customElements.define = function (
    this: CustomElementRegistry,
    name: string,
    ctor: CustomElementConstructor,
    defineOptions?: ElementDefinitionOptions
  ) {
    let record = state.records.get(name);
    if (record === undefined && this.get(name) !== undefined) {
      // Defined before install() ran (shouldn't happen — the installer is
      // import-hoisted — but recover gracefully without instance tracking).
      record = {
        tagName: name,
        canonical: this.get(name)!,
        instances: new Set(),
        generation: 0,
      };
      state.records.set(name, record);
    }
    if (record === undefined) {
      record = {
        tagName: name,
        canonical: ctor,
        instances: new Set(),
        generation: 0,
      };
      state.records.set(name, record);
      state.tagOfVersion.set(ctor, name);
      instrument(state, ctor.prototype, record);
      return nativeDefine.call(this, name, ctor, defineOptions);
    }
    if (ctor === record.canonical) {
      // Same class object re-defined; nothing to patch.
      return;
    }
    state.tagOfVersion.set(ctor, name);
    hotPatch(state, record, ctor as ReactiveCtorLike);
  };

  // A module evaluated again exports a later version of its class, which
  // the registry never holds, so `getName()` would not know it. Code that
  // maps a class to its tag (Astro's client renderer, given the class an
  // island imports) asks with that version, and the tag is still right.
  // Where the browser has no `getName()` (Safari before 17), this one is
  // all there is, and knows the classes defined since install.
  // oxlint-disable-next-line typescript/unbound-method
  const nativeGetName = customElements.getName as
    | CustomElementRegistry['getName']
    | undefined;
  customElements.getName = function (
    this: CustomElementRegistry,
    ctor: CustomElementConstructor
  ) {
    return (
      nativeGetName?.call(this, ctor) ?? state.tagOfVersion.get(ctor) ?? null
    );
  };
};
